import React from 'react';
import { AirportSmallDto } from '../../../airports/airport-dto';
import { FlightSearchResult } from '../../../flights/interop/flight-search-result';
import commonFormatter from '../../../formatters/common-formatter';
import { MessageType } from '../../../services/actor-messages';
import { EventBus } from '../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { useFlightWorker } from '../../hooks/use-flight-worker';
import { toggleLeftFlyout } from '../../state/slices/workspace-slice';
import { Command } from './types';

const MIN_TERM_LENGTH = 3;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;

export function useSearchCommands(query: string): Command[] {
    const worker = useFlightWorker();
    const dispatch = useAppDispatch();
    const airportDisplayStyle = useAppSelector((x) => x.settings.airportIdDisplayStyle);
    const [flights, setFlights] = React.useState<FlightSearchResult[]>([]);
    const [airports, setAirports] = React.useState<AirportSmallDto[]>([]);
    const reqRef = React.useRef(0);

    React.useEffect(() => {
        const term = query.trim();
        if (term.length < MIN_TERM_LENGTH) {
            setFlights([]);
            setAirports([]);
            return;
        }
        const id = ++reqRef.current;
        const timeout = self.setTimeout(async () => {
            const [flightResults, airportResults] = await Promise.all([
                worker.actor.sendAsync({ type: MessageType.queryFlightsSearch, data: term }).catch(() => [] as FlightSearchResult[]),
                worker.actor.sendAsync({ type: MessageType.queryAirportSearch, data: term }).catch(() => [] as AirportSmallDto[])
            ]);
            if (reqRef.current !== id) {
                return;
            }
            setFlights(flightResults);
            setAirports(airportResults);
        }, DEBOUNCE_MS);

        return () => self.clearTimeout(timeout);
    }, [query, worker]);

    return React.useMemo<Command[]>(() => {
        const commands: Command[] = [];

        for (const flight of flights.slice(0, MAX_RESULTS)) {
            const origin = commonFormatter.formatAirportId(flight.originAirport, airportDisplayStyle) ?? flight.originFallback ?? 'N/A';
            const destination =
                commonFormatter.formatAirportId(flight.destinationAirport, airportDisplayStyle) ?? flight.destinationFallback ?? 'N/A';
            commands.push({
                id: `flight-${flight.id}`,
                title: flight.acid,
                subtitle: `${origin} → ${destination}`,
                keywords: [flight.acid],
                group: 'Flights',
                run: () => EventBus.fire('select-flight', { flightId: flight.id, options: { source: 'search' } })
            });
        }

        for (const airport of airports.slice(0, MAX_RESULTS)) {
            const airportId = airport.iata || airport.faa || airport.icao || '';
            const label = [airport.iata || airport.faa || '', airport.icao || ''].filter((s) => s.trim() !== '').join(' / ');
            commands.push({
                id: `airport-${airportId}`,
                title: label || airportId,
                subtitle: airport.name ?? undefined,
                keywords: [airportId, airport.name ?? ''],
                group: 'Airports',
                run: () => dispatch(toggleLeftFlyout({ type: 'airport', airportId }))
            });
        }

        return commands;
    }, [flights, airports, airportDisplayStyle, dispatch]);
}
